import { EnvironmentProviders, inject, InjectionToken, provideAppInitializer, Provider } from '@angular/core';

import { AutocompleteService } from '../Domain/Port/autocomplete.service';
import { GeocodingService } from '../Domain/Port/geocoding.service';
import { GoogleAutocompleteService } from '../Infrastructure/Adapter/Google/Autocomplete/google-autocomplete.service';
import { GoogleGeocodingService } from '../Infrastructure/Adapter/Google/Geocoding/google-geocoding.service';
import { GoogleMapsLoader } from '../Infrastructure/google-maps-loader.service';

/**
 * Injection token for the Google Maps API key.
 * This token is used to provide the API key to the GoogleMapsLoader service.
 */
export const GOOGLE_MAPS_API_KEY_IT = new InjectionToken<string>('GOOGLE_MAPS_API_KEY_IT');

/**
 * Helper function that creates a factory for loading Google Maps.
 * This function is used with provideAppInitializer to ensure Google Maps
 * is loaded during application initialization.
 *
 * @returns A factory function that injects the GoogleMapsLoader service and calls its load method
 */
const googleMapLoadFunc = () => {
    return () => {
        const loader = inject(GoogleMapsLoader);
        loader.load();
    };
};

/**
 * Provides the necessary providers for using Google Maps in an Angular application.
 * This function sets up:
 * 1. Google Maps API key injection token
 * 2. An app initializer that loads the Google Maps JavaScript API during application startup
 *
 * @param apiKey - The Google Maps API key to use for authentication
 * @returns An array of providers that should be included in the application's providers array
 *
 * @example
 * // In your app.config.ts:
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     // ... other providers
 *     provideGoogleMaps('your-google-maps-api-key')
 *   ]
 * };
 */
export const provideGoogleMapsServices = (apiKey: string): Array<Provider | EnvironmentProviders> => {
    return [
        provideAppInitializer(googleMapLoadFunc()),
        {
            provide: GOOGLE_MAPS_API_KEY_IT,
            useValue: apiKey
        },
        {
            provide: GeocodingService,
            useExisting: GoogleGeocodingService
        },
        {
            provide: AutocompleteService,
            useExisting: GoogleAutocompleteService
        }
    ];
};
